import { API_BASE, authHeaders } from './apiAuth'

/**
 * Reported pictures, the trash and the Egnyte Inbox, as the pages see them
 * (workers/api/src/routes/libraryAdmin.ts). Administrators only.
 */

export type AdminLibrary = 'audit' | 'visual-language' | 'design-works' | 'egnyte'

export interface ReportTag {
  id: string
  name: string
  group: string
  /** 'ai', 'manual' or 'frame'. */
  source: string
  /** Proposed by Auto Filter and still waiting for its threshold. */
  pending: boolean
}

export interface ReportedPicture {
  assetId: string
  title: string
  thumb: string
  reportedBy: string[]
  reportedAt: string
  /** Auto Filter is still tagging it again. */
  retagging: boolean
  tags: ReportTag[]
}

export interface ReportsView {
  total: number
  groups: { slug: string; label: string }[]
  tags: { id: string; name: string; group: string }[]
  reports: ReportedPicture[]
}

export interface TrashedPicture {
  assetId: string
  title: string
  thumb: string
  trashedAt: string
  trashedBy: string
  /** When the nightly job deletes it for good. */
  goneAt: string
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(await authHeaders('web')) },
  })
  if (response.status === 403) throw new Error('Administrators only')
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(body?.error ?? `HTTP ${response.status}`)
  }
  return (await response.json()) as T
}

const post = <T>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body) })

export function readReports(library: 'audit' | 'design-works' | 'visual-language'): Promise<ReportsView> {
  return call(`/api/library-admin/reports?library=${library}`)
}

/** Tags put on (by hand) and taken off (never put back by Auto Filter). */
export function changeReportTags(
  library: 'audit' | 'design-works' | 'visual-language',
  assetId: string,
  change: { add?: string[]; remove?: string[] },
): Promise<unknown> {
  return post('/api/library-admin/report-tags', { library, assetId, add: change.add ?? [], remove: change.remove ?? [] })
}

/** The picture is right as it is now: it leaves the Reported list. */
export function keepReported(library: 'audit' | 'design-works' | 'visual-language', assetId: string): Promise<unknown> {
  return post('/api/library-admin/report-keep', { library, assetId })
}

export function trashPictures(library: AdminLibrary, assetIds: string[]): Promise<{ trashed: number }> {
  return post('/api/library-admin/trash', { library, assetIds })
}

export function readTrash(library: AdminLibrary): Promise<{ days: number; pictures: TrashedPicture[] }> {
  return call(`/api/library-admin/trash?library=${library}`)
}

export function restorePictures(library: AdminLibrary, assetIds: string[]): Promise<{ restored: number }> {
  return post('/api/library-admin/restore', { library, assetIds })
}

/** Egnyte Inbox -> Design Works, with their Project Name and Type. */
export function sendToDesignWorks(assetIds: string[]): Promise<{ sent: number }> {
  return post('/api/library-admin/egnyte-send', { assetIds })
}

/* Clients (docs/clients.sql) */

/** Client names in use, and the letters remembered for each. */
export function readClients(): Promise<{ clients: string[]; codes: { code: string; client: string }[] }> {
  return call('/api/library-admin/clients')
}

/**
 * Give Egnyte Inbox pictures a client (it replaces any they had). Their
 * project letters not remembered yet are remembered for it; `already` lists
 * letters that mean another client and were left alone.
 */
export function giveClient(
  assetIds: string[],
  name: string,
): Promise<{ tagged: number; remembered: string[]; already: { code: string; client: string }[] }> {
  return post('/api/library-admin/egnyte-client', { assetIds, name })
}

/* One picture's tags, from the long press on a library picture (2026-10-12) */

export interface PictureTagsView {
  /** The library's categories, in the filter's order (hidden ones left out). */
  groups: { slug: string; label: string }[]
  /** Every tag in the library that can be put on. */
  tags: { id: string; name: string; group: string }[]
  /** What the picture carries now in this library. */
  on: ReportTag[]
}

export function readPictureTags(library: AdminLibrary, assetId: string): Promise<PictureTagsView> {
  return call(`/api/library-admin/picture-tags?library=${library}&assetId=${encodeURIComponent(assetId)}`)
}

/**
 * Put tags on and take them off; `create` makes a tag that does not exist yet
 * in that category, then puts it on. Hands back the picture's tags as they now
 * are. Taken off by hand means Auto Filter never puts it back.
 */
export function changePictureTags(
  library: AdminLibrary,
  assetId: string,
  change: { add?: string[]; remove?: string[]; create?: { group: string; name: string }[] },
): Promise<PictureTagsView> {
  return post('/api/library-admin/picture-tags', {
    library,
    assetId,
    add: change.add ?? [],
    remove: change.remove ?? [],
    create: change.create ?? [],
  })
}

/* ---------------------------------------------------------------------------
 * The Review Queue (Huanrui, 2026-10-15): what waits, and possible duplicates
 * ------------------------------------------------------------------------- */

/** How many things wait in the Review Queue. */
export function readReviewCounts(): Promise<{ reported: number; duplicates: number }> {
  return call('/api/library-admin/review-counts')
}

export interface LookalikePicture {
  assetId: string
  title: string
  thumb: string
  width: number
  height: number
  source: string
  addedAt: string
  libraries: string[]
  tags: { name: string; library: string }[]
}

export interface LookalikePair {
  id: string
  /** 'same': almost certainly one picture. 'maybe': worth a look. */
  band: 'same' | 'maybe'
  /** Shape bits apart, of 64. */
  distance: number
  /** Colours apart, 0 to about 441. */
  colourGap: number
  foundAt: string
  /** The one that came in, and the one already there. */
  incoming: LookalikePicture
  existing: LookalikePicture
}

export function readLookalikes(library: AdminLibrary | 'all'): Promise<{ total: number; pairs: LookalikePair[] }> {
  return call(`/api/library-admin/lookalikes?library=${library}`)
}

/** One picture kept; the other's tags and libraries put on it, the other to the trash. */
export function mergeLookalike(pairId: string, keep: string): Promise<{ trashed: number }> {
  return post('/api/library-admin/lookalike-merge', { pairId, keep })
}

/** Two different pictures: never asked about again. */
export function markLookalikesDifferent(pairId: string): Promise<{ ok: boolean }> {
  return post('/api/library-admin/lookalike-different', { pairId })
}
